import React, { ReactElement, useEffect, useRef } from 'react'
import { AmazonPayload } from './types/AmazonPayload'
import { DeliveryOption } from './types/DeliveryOptions'

type AmazonPayButtonProps = {
  amazonPayload: AmazonPayload;
  canCheckout: boolean;
  deliveryOptionState: DeliveryOption;
  railsEnv: string;
}

declare global {
  interface Window {
    amazon?: {
      Pay: {
        renderButton: (selector: string, config: object) => AmazonPayButtonInstance
      }
    }
  }
}

interface AmazonPayButtonInstance {
  onClick: (callback: () => void) => void
  initCheckout: (config) => void
}

// Ensure SDK is loaded only once
let amazonPaySdkPromise: Promise<void> | null = null

const loadAmazonPaySdk = (): Promise<void> => {
  if (window.amazon) {
    return Promise.resolve()
  }

  if (!amazonPaySdkPromise) {
    amazonPaySdkPromise = new Promise((resolve) => {
      const script = document.createElement('script')
      script.src = 'https://static-eu.payments-amazon.com/checkout.js'
      script.onload = () => resolve()
      document.body.appendChild(script)
    })
  }

  return amazonPaySdkPromise
}

const AmazonPayButton = ({ amazonPayload, canCheckout, deliveryOptionState, railsEnv }: AmazonPayButtonProps): ReactElement => {
  const mobileWidth = '100%'
  const desktopWidth = '170px'
  const sandbox = railsEnv !== 'production'

  const canCheckoutRef = useRef(canCheckout)

  useEffect(() => {
    canCheckoutRef.current = canCheckout
  }, [canCheckout])

  const getWidth = (): string => window.innerWidth < 640 ? mobileWidth : desktopWidth
  const getContainerWidth = (): string => window.innerWidth < 640 ? '98%' : desktopWidth

  useEffect(() => {
    const renderButton = () => {
      if (!window.amazon) return

      // Instruct Amazon if we need to collect a delivery address or not
      const productType = deliveryOptionState !== 'deliver-to-postcode' ? 'PayOnly' : 'PayAndShip'

      const button = window.amazon.Pay.renderButton('#AmazonPayButton', {
        merchantId: amazonPayload.merchant_id,
        ledgerCurrency: 'GBP',
        sandbox,
        checkoutLanguage: 'en_GB',
        productType,
        placement: 'Cart',
        buttonColor: 'Gold'
      })

      button.onClick(() => {
        if (canCheckoutRef.current) {
          button.initCheckout({
            createCheckoutSessionConfig: {
              payloadJSON: amazonPayload.payload,
              signature: amazonPayload.signature,
              publicKeyId: amazonPayload.public_key_id
            }
          })
        }
      })
    }

    loadAmazonPaySdk().then(renderButton)
  }, [amazonPayload, deliveryOptionState])

  const [buttonWidth, setButtonWidth] = React.useState(getWidth())
  const [containerWidth, setContainerWidth] = React.useState(getContainerWidth())

  // Avoids visual glitch with Amazon button when resizing window (cannot be achieved with css)
  React.useEffect((): void => {
    const handleResize = (): void => {
      setButtonWidth(getWidth())
      setContainerWidth(getContainerWidth())
    }
    window.addEventListener('resize', handleResize)
  })

  return (
    <div className='amazon-pay-checkout-container'>
      <div className='amazon-pay-checkout' style={{ display: 'block', width: containerWidth }} >
        <div
          id="AmazonPayButton"
          key={deliveryOptionState} // Forces fresh DOM element
          style={{ display: 'flex', height: '55px', marginTop: '1px', width: buttonWidth }} />
      </div>
    </div>
  )
}

export default AmazonPayButton
